import { reactive } from 'vue';
import { ErrorPopup } from '../typings/games';

interface LaunchError {
  step: string;
  code: string;
  message: string;
  details?: string;
  retryable: boolean;
  link?: { label: string; href?: string; external?: boolean } | null;
}

interface LaunchState {
  gameId: string | null;
  progress: number | null;
  speed: string;
  phase: string | null;
  detail: string;
  error: LaunchError | null;
}

interface DiagnosticSub {
  l: string;
  v: string;
  status: string;
}

interface DiagnosticStep {
  name: string;
  hint: string;
  status: string;
  progress: number;
  subs: DiagnosticSub[];
}

interface DiagnosticState {
  status: string;
  path: string;
  content: string;
  steps: DiagnosticStep[];
}

interface SettingsRequest {
  tab: string;
  token: number;
}

interface UiState {
  error: string;
  errorModal: ErrorPopup;
  launch: LaunchState;
  diagnostic: DiagnosticState;
  settings: SettingsRequest;
}

function emptyErrorModal(): ErrorPopup {
  return {
    isOpen: false,
    title: '',
    messages: [],
    primaryButton: '',
    secondaryButton: '',
    primaryAction: '',
    secondaryAction: '',
    critical: false,
  };
}

export const ui = reactive<UiState>({
  error: '',
  errorModal: emptyErrorModal(),
  launch: {
    gameId: null,
    progress: null,
    speed: '',
    phase: null,
    detail: '',
    error: null,
  },
  diagnostic: {
    status: 'idle',
    path: '',
    content: '',
    steps: [],
  },
  settings: {
    tab: '',
    token: 0,
  },
});

// Ask the shell (Default layout) to open the settings modal on a given page. The token forces a
// re-open even when the requested page is unchanged (e.g. triggered twice from the tray).
export function openSettingsPanel(tab: string): void {
  ui.settings.tab = tab;
  ui.settings.token += 1;
}

export function setDiagnosticProgress(payload: any): void {
  const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
  if (!data) {
    return;
  }
  if (Array.isArray(data.steps)) {
    ui.diagnostic.steps = data.steps;
  }
  if (data.status) {
    ui.diagnostic.status = data.status;
  }
}

export function setDiagnosticResult(payload: any): void {
  const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
  if (data && (data.status === 'success' || data.path)) {
    ui.diagnostic.status = 'done';
    ui.diagnostic.path = data.path || '';
    ui.diagnostic.content = data.content || '';
  } else {
    ui.diagnostic.status = 'error';
  }
}

export function showError(message: string): void {
  ui.error = message;
}

export function dismissError(): void {
  ui.error = '';
}

export function displayErrors(
  title: string,
  messages: string[],
  primaryButton: string,
  secondaryButton: string,
  primaryAction: string,
  secondaryAction: string,
  critical = false,
): void {
  ui.errorModal = {
    isOpen: true,
    title,
    messages,
    primaryButton,
    secondaryButton,
    primaryAction,
    secondaryAction,
    critical,
  };
}

export function closeErrorModal(): void {
  ui.errorModal.isOpen = false;
}

export function setProgress(gameId: string | null, progress: number): void {
  if (gameId !== null) {
    ui.launch.gameId = gameId;
  }
  ui.launch.progress = progress;
}

export function setDownloadingSpeed(speed: string): void {
  ui.launch.speed = speed;
}

// The native launcher declares its launch phase explicitly (no front-side guessing).
export function setLaunchState(state: {
  phase?: string;
  progress?: number;
  detail?: string;
  step?: string;
  code?: string;
  message?: string;
  details?: string;
  retryable?: boolean;
  link?: { label: string; href?: string; external?: boolean };
}): void {
  if (typeof state.phase === 'string') {
    ui.launch.phase = state.phase;
  }
  if (typeof state.progress === 'number') {
    ui.launch.progress = state.progress;
  }
  ui.launch.detail = typeof state.detail === 'string' ? state.detail : '';
  if (state.phase === 'ERROR') {
    ui.launch.error = {
      step: typeof state.step === 'string' ? state.step : 'PREPARING',
      code: typeof state.code === 'string' ? state.code : 'UNKNOWN',
      message: typeof state.message === 'string' ? state.message : 'Le lancement a échoué',
      details: typeof state.details === 'string' ? state.details : undefined,
      retryable: state.retryable !== false,
      link: state.link || null,
    };
  } else {
    ui.launch.error = null;
  }
}

export function resetLaunch(): void {
  ui.launch.gameId = null;
  ui.launch.progress = null;
  ui.launch.speed = '';
  ui.launch.phase = null;
  ui.launch.detail = '';
  ui.launch.error = null;
}

// Bridge the native (Java/CEF) callbacks to the shared UI state, once for the whole app.
export function installNativeBridge(): void {
  const w: any = window;

  w.showError = (message: string) => showError(message);

  w.displayErrors = (
    title: string,
    messages: string[],
    primaryButton: string,
    secondaryButton: string,
    primaryAction: string,
    secondaryAction: string,
    critical = false,
  ) => displayErrors(title, messages, primaryButton, secondaryButton, primaryAction, secondaryAction, critical);

  w.setProgress = (progress: number) => setProgress(ui.launch.gameId, progress);

  w.downloadingSpeed = (speed: string) => setDownloadingSpeed(speed);

  w.launcherState = (state: any) => {
    try {
      setLaunchState(typeof state === 'string' ? JSON.parse(state) : state);
    } catch (e) {
      // Malformed payload: keep the last known state.
    }
  };

  w.diagnosticResult = (payload: any) => setDiagnosticResult(payload);

  w.diagnosticProgress = (payload: any) => setDiagnosticProgress(payload);

  w.openSettings = (tab: string) => openSettingsPanel(typeof tab === 'string' ? tab : '');

  w.openDiagnostic = () => openSettingsPanel('diag');
}
